import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { RowLink } from "../RowLink";
import { useMe, canReadAllMembers } from "../../auth/queries";
import { personDetailHref } from "./personDetailParam";
import type { ID } from "../../lib/id";
import styles from "./PersonDetail.module.css";

/**
 * PersonLink is a person's name that opens the person detail dialog. The
 * dialog reads the whole registry, so only its readers (canReadAllMembers) get the
 * link; everybody else sees the plain name (a link that 403s is worse than
 * none, the RowLink rule).
 *
 * By default it is a RowLink, the bold name cell of a table. `inline` keeps
 * the surrounding text's weight instead, for a place where weight already
 * means something: „Možné duplicity" bolds only the values that differ.
 */
export function PersonLink({
  personId,
  inline = false,
  children,
}: {
  personId: ID;
  inline?: boolean;
  children: ReactNode;
}) {
  const { data: me } = useMe();
  const location = useLocation();
  if (!canReadAllMembers(me)) return <>{children}</>;
  const to = personDetailHref(location.pathname, location.search, personId);
  if (inline) {
    return (
      <Link to={to} className={styles.inlineLink}>
        {children}
      </Link>
    );
  }
  return <RowLink to={to}>{children}</RowLink>;
}
